import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, X, Edit2, Search, SlidersHorizontal, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useIsMobile } from "@/hooks/useIsMobile";
import { VENDOR_TYPE_LABELS } from "@/lib/projectUtils";

const TABS = [
  { key: "events", label: "Events" },
  { key: "services", label: "Services" },
  { key: "vendors", label: "Vendors" },
  { key: "editors", label: "Editors" },
  { key: "expenses", label: "Expense categories" },
];

export default function Catalog() {
  const [tab, setTab] = useState("events");
  const isMobile = useIsMobile();
  return (
    <Layout>
      <div className="fade-up" data-testid="catalog-page">
        {/* Eyebrow + title — mobile uses shorter eyebrow + 34px title */}
        <div
          style={{
            fontSize: isMobile ? 11 : 10,
            fontWeight: isMobile ? 800 : 700,
            letterSpacing: isMobile ? "0.18em" : "0.18em",
            textTransform: "uppercase",
            color: "#B08D55",
            marginBottom: isMobile ? 4 : 8,
          }}
        >
          {isMobile ? "STUDIO · CATALOG" : "CATALOG"}
        </div>
        <h1
          className="font-display catalog-h1"
          style={{
            fontSize: isMobile ? 34 : 52,
            lineHeight: isMobile ? 1 : 1.05,
            color: "#22382C",
            marginBottom: isMobile ? 20 : 24,
            fontWeight: 500,
          }}
          data-testid="catalog-title"
        >
          Catalog
        </h1>

        {/* Phase 7.1 — Mobile-only visual tweaks. Below 1024px:
              • Sub-tabs render as pill-style horizontally-scrollable row
                (active = dark ivory fill matching the Delivery/Accounts pattern)
              • Tighten the white content-wrapper padding
              • Hide the desktop CSS-grid column-header rows in
                Vendors/Editors tabs (rows become stacked cards below)
              • Search input (Vendors) gets full width instead of 260px */}
        <style>{`
          @media (max-width: 1023px) {
            [data-testid="catalog-tabs"] {
              border-bottom: none !important;
              flex-wrap: nowrap !important;
              overflow-x: auto !important;
              margin: 0 -16px 20px !important;
              padding: 2px 16px 8px !important;
              gap: 8px;
              scrollbar-width: none;
            }
            [data-testid="catalog-tabs"]::-webkit-scrollbar { display: none; }
            .catalog-wrapper { padding: 0 !important; background: transparent !important; border: none !important; box-shadow: none !important; border-radius: 0 !important; }
            .catalog-col-header { display: none !important; }
            [data-testid="vendors-search"] { width: 100% !important; }
          }
        `}</style>

        {/* Tab bar */}
        <div
          data-testid="catalog-tabs"
          style={{
            display: "flex",
            borderBottom: "1px solid #EDE5D8",
            marginBottom: 24,
            flexWrap: "wrap",
          }}
        >
          {TABS.map((t) => {
            const active = tab === t.key;
            if (isMobile) {
              // Pill-style mobile tab
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`catalog-tab-${t.key}`}
                  style={{
                    padding: "9px 15px",
                    fontSize: 12,
                    letterSpacing: "0.06em",
                    color: active ? "#F7F3EC" : "#22382C",
                    background: active ? "#22382C" : "#FFFDFA",
                    border: active ? "1px solid #22382C" : "1px solid rgba(34,56,44,0.10)",
                    borderRadius: 999,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    minHeight: 36,
                    cursor: "pointer",
                    transition: "background .15s, color .15s",
                  }}
                >
                  {t.label}
                </button>
              );
            }
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`catalog-tab-${t.key}`}
                style={{
                  padding: "14px 18px",
                  fontSize: 11,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: active ? "#22382C" : "#76705F",
                  borderBottom: active ? "2.5px solid #22382C" : "2.5px solid transparent",
                  fontWeight: active ? 600 : 500,
                  marginBottom: -1,
                  transition: "color .15s, border-bottom-color .15s",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Content wrapper card */}
        <div
          className="catalog-wrapper"
          style={{
            background: "#FFFFFF",
            border: "1px solid #E9E2D6",
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 2px 6px rgba(60,46,20,.05)",
          }}
        >
          {tab === "events" && <SimpleCatalogTab type="event_types" label="event type" />}
          {tab === "services" && <ServicesTab />}
          {tab === "vendors" && <VendorsTab />}
          {tab === "editors" && <EditorsTab />}
          {tab === "expenses" && <SimpleCatalogTab type="expense_categories" label="expense category" />}
        </div>
      </div>
    </Layout>
  );
}

function SimpleCatalogTab({ type, label }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState("");
  const isMobile = useIsMobile();

  const refresh = async () => {
    const { data } = await api.get("/catalogs");
    setItems((data[type] || []).sort((a, b) => a.name.localeCompare(b.name)));
  };
  useEffect(() => { refresh(); }, []);

  const addItem = async () => {
    if (!adding.trim()) return;
    try {
      await api.post(`/catalogs/${type}`, { name: adding.trim() });
      setAdding("");
      await refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Add failed"); }
  };

  const rename = async (it, name) => {
    try {
      await api.patch(`/catalogs/${type}/${it.id}`, { name });
      await refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const toggle = async (it) => {
    await api.patch(`/catalogs/${type}/${it.id}`, { active: !it.active });
    refresh();
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    await api.delete(`/catalogs/${type}/${it.id}`);
    refresh();
  };

  if (isMobile) {
    // ============ MOBILE BRANCH ============
    return (
      <div data-testid={`tab-${type}`}>
        <input
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addItem()}
          placeholder={`Add ${label}…`}
          data-testid={`${type}-add-input`}
          style={{
            width: "100%",
            background: "#FFFDFA",
            border: "1px solid rgba(34,56,44,0.10)",
            borderRadius: 14,
            padding: "0 14px",
            height: 48,
            fontSize: 14,
            color: "#22382C",
            outline: "none",
            marginBottom: 10,
          }}
        />
        <button
          onClick={addItem}
          data-testid={`${type}-add-btn`}
          style={{
            width: "100%",
            background: "#22382C",
            color: "#F7F3EC",
            border: "none",
            borderRadius: 13,
            height: 46,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.05em",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            cursor: "pointer",
            marginBottom: 18,
          }}
        >
          <Plus size={14} /> Add
        </button>
        {items.length === 0 ? (
          <div
            style={{
              border: "1.5px dashed rgba(34,56,44,0.15)",
              borderRadius: 16,
              padding: "44px 16px",
              textAlign: "center",
              background: "#FBF8F1",
            }}
          >
            <div style={{ fontFamily: "var(--ds-num-font)", fontSize: 30, color: "rgba(34,56,44,0.28)", lineHeight: 1, marginBottom: 10 }}>—</div>
            <div style={{ fontSize: 13, color: "rgba(34,56,44,0.5)" }}>No {label}s yet.</div>
          </div>
        ) : (
          <div
            style={{
              background: "#FFFDFA",
              border: "1px solid rgba(34,56,44,0.10)",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            {items.map((it, i) => (
              <CatalogRowMobile
                key={it.id}
                item={it}
                first={i === 0}
                onToggle={toggle}
                onRemove={remove}
                testIdPrefix={type}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ============ DESKTOP BRANCH (original) ============
  return (
    <div data-testid={`tab-${type}`}>
      {/* Add row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <input
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addItem()}
          placeholder={`Add ${label}…`}
          data-testid={`${type}-add-input`}
          style={{
            flex: 1,
            background: "#FCFAF5",
            border: "1px solid #E4DCCD",
            borderRadius: 8,
            padding: "11px 14px",
            fontSize: 14,
            color: "#2A2722",
            outline: "none",
          }}
        />
        <button
          onClick={addItem}
          data-testid={`${type}-add-btn`}
          style={{
            background: "#22382C",
            color: "#F4EFE4",
            border: "none",
            borderRadius: 6,
            padding: "11px 20px",
            fontSize: 13,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            transition: "background-color .15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#152823")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#22382C")}
        >
          <Plus size={14} /> Add
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {items.map((it) => (
          <CatalogRow key={it.id} item={it} onRename={rename} onToggle={toggle} onRemove={remove} testIdPrefix={type} />
        ))}
        {items.length === 0 && <p style={{ fontSize: 13, fontStyle: "italic", color: "#76705F" }}>No items yet.</p>}
      </div>
    </div>
  );
}

// Mobile row inside a grouped card: label + [Active dark-square checkbox] + trash
function CatalogRowMobile({ item, first, onToggle, onRemove, testIdPrefix }) {
  return (
    <div
      data-testid={`${testIdPrefix}-row-${item.id}`}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "14px 15px",
        borderTop: first ? "none" : "1px solid rgba(34,56,44,0.07)",
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 600, color: item.active ? "#22382C" : "#A39A8B", minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {item.name}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            minHeight: 44,
          }}
        >
          <input
            type="checkbox"
            checked={item.active}
            onChange={() => onToggle(item)}
            data-testid={`${testIdPrefix}-toggle-${item.id}`}
            style={{ width: 20, height: 20, accentColor: "#22382C" }}
          />
          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(34,56,44,0.55)" }}>Active</span>
        </label>
        <button
          onClick={() => onRemove(item)}
          title="Delete"
          data-testid={`${testIdPrefix}-delete-${item.id}`}
          aria-label="Delete"
          style={{
            width: 44,
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "rgba(162,75,59,0.7)",
          }}
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

function CatalogRow({ item, onRename, onToggle, onRemove, testIdPrefix }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(item.name);
  return (
    <div
      data-testid={`${testIdPrefix}-row-${item.id}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderRadius: 10,
        padding: "7px 9px",
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        transition: "box-shadow .15s ease, transform .15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 5px 14px rgba(60,46,20,.10)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {editing ? (
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={() => { onRename(item, val); setEditing(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setVal(item.name); setEditing(false); } }}
          style={{
            flex: 1,
            background: "#FCFAF5",
            border: "1px solid #E4DCCD",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 14,
            color: "#2A2722",
            outline: "none",
          }}
        />
      ) : (
        <button
          onClick={() => setEditing(true)}
          style={{
            flex: 1,
            textAlign: "left",
            fontSize: 13,
            fontWeight: 500,
            color: item.active ? "#2A2722" : "#A39A8B",
          }}
        >
          {item.name}
          {!item.active && (
            <span style={{
              marginLeft: 8,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#A39A8B",
            }}>
              INACTIVE
            </span>
          )}
        </button>
      )}
      <label
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "#76705F",
          display: "flex",
          alignItems: "center",
          gap: 6,
          minHeight: 24,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={item.active}
          onChange={() => onToggle(item)}
          data-testid={`${testIdPrefix}-toggle-${item.id}`}
        />
        Active
      </label>
      <button
        onClick={() => onRemove(item)}
        title="Delete"
        data-testid={`${testIdPrefix}-delete-${item.id}`}
        style={{
          color: "#A39A8B",
          minWidth: 24,
          minHeight: 24,
          padding: 5,
          borderRadius: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "color .15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#A35C45")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#A39A8B")}
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

function ServicesTab() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState("");
  const [expanded, setExpanded] = useState(null);
  const isMobile = useIsMobile();

  const refresh = async () => {
    const { data } = await api.get("/catalogs");
    setItems((data.service_types || []).sort((a, b) => a.name.localeCompare(b.name)));
  };
  useEffect(() => { refresh(); }, []);

  const addItem = async () => {
    if (!adding.trim()) return;
    try {
      await api.post(`/catalogs/service_types`, { name: adding.trim(), has_deliverables: false, default_deliverables: [] });
      setAdding("");
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Add failed"); }
  };

  const patch = async (it, body) => {
    try {
      await api.patch(`/catalogs/service_types/${it.id}`, body);
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    await api.delete(`/catalogs/service_types/${it.id}`);
    refresh();
  };

  return (
    <div data-testid="tab-service_types">
      {isMobile ? (
        <>
          <input
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addItem()}
            placeholder="Add service type…"
            data-testid="service_types-add-input"
            style={{
              width: "100%",
              background: "#FFFDFA",
              border: "1px solid rgba(34,56,44,0.10)",
              borderRadius: 14,
              padding: "0 14px",
              height: 48,
              fontSize: 14,
              color: "#22382C",
              outline: "none",
              marginBottom: 10,
            }}
          />
          <button
            onClick={addItem}
            data-testid="service_types-add-btn"
            style={{
              width: "100%",
              background: "#22382C",
              color: "#F7F3EC",
              border: "none",
              borderRadius: 13,
              height: 46,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.05em",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              cursor: "pointer",
              marginBottom: 18,
            }}
          >
            <Plus size={14} /> Add
          </button>
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
          <input
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addItem()}
            placeholder="Add service type…"
            data-testid="service_types-add-input"
            style={{
              flex: 1,
              background: "#FCFAF5",
              border: "1px solid #E4DCCD",
              borderRadius: 8,
              padding: "11px 14px",
              fontSize: 14,
              color: "#2A2722",
              outline: "none",
            }}
          />
          <button
            onClick={addItem}
            data-testid="service_types-add-btn"
            style={{
              background: "#22382C",
              color: "#F4EFE4",
              border: "none",
              borderRadius: 6,
              padding: "11px 20px",
              fontSize: 13,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
            }}
          >
            <Plus size={14} /> Add
          </button>
        </div>
      )}
      <div
        style={
          isMobile
            ? {
                background: "#FFFDFA",
                border: "1px solid rgba(34,56,44,0.10)",
                borderRadius: 16,
                overflow: "hidden",
              }
            : { display: "flex", flexDirection: "column", gap: 5 }
        }
      >
        {items.map((it, i) => (
          <ServiceRow
            key={it.id}
            item={it}
            expanded={expanded === it.id}
            onExpand={() => setExpanded(expanded === it.id ? null : it.id)}
            onPatch={patch}
            onRemove={remove}
            mobile={isMobile}
            first={i === 0}
          />
        ))}
      </div>
    </div>
  );
}

const EDITABLE_SERVICES = ["Candid Photo", "Candid Video", "Traditional Photo", "Traditional Video"];
const DELIVERABLE_UNITS = ["photos", "reels", "films", "videos", "albums", "sets"];

function ServiceRow({ item, expanded, onExpand, onPatch, onRemove, mobile, first }) {
  const [editName, setEditName] = useState(false);
  const [nameVal, setNameVal] = useState(item.name);
  const [defs, setDefs] = useState(item.default_deliverables || []);
  const isEditable = EDITABLE_SERVICES.includes(item.name);

  useEffect(() => { setNameVal(item.name); setDefs(item.default_deliverables || []); }, [item]);

  const saveName = () => {
    if (nameVal.trim() && nameVal !== item.name) onPatch(item, { name: nameVal.trim() });
    setEditName(false);
  };

  const saveDefs = () => onPatch(item, { default_deliverables: defs });
  const updateDef = (i, patch) => setDefs(defs.map((d, j) => j === i ? { ...d, ...patch } : d));
  const addDef = () => setDefs([...defs, { deliverable_type: "", quantity: 1, unit: "photos", due_days_from_shoot: 10 }]);
  const removeDef = (i) => setDefs(defs.filter((_, j) => j !== i));

  return (
    <div
      data-testid={`service-row-${item.id}`}
      style={mobile ? {
        background: "transparent",
        borderTop: first ? "none" : "1px solid rgba(34,56,44,0.07)",
        overflow: "hidden",
      } : {
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderRadius: 10,
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        overflow: "hidden",
        transition: "box-shadow .15s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16, padding: mobile ? "12px 15px" : "7px 9px" }}>
        {editName ? (
          <input
            autoFocus value={nameVal} onChange={(e) => setNameVal(e.target.value)} onBlur={saveName}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setNameVal(item.name); setEditName(false); } }}
            style={{
              flex: 1,
              background: "#FCFAF5",
              border: "1px solid #E4DCCD",
              borderRadius: 6,
              padding: "6px 10px",
              fontSize: 14,
              color: "#2A2722",
              outline: "none",
            }}
          />
        ) : (
          <button
            onClick={() => setEditName(true)}
            style={{
              flex: 1,
              textAlign: "left",
              fontSize: 13,
              fontWeight: 500,
              color: item.active ? "#2A2722" : "#A39A8B",
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span>{item.name}</span>
            {!item.active && (
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "#A39A8B" }}>
                INACTIVE
              </span>
            )}
            {isEditable && (
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#B08D55" }}>
                EDITABLE
              </span>
            )}
          </button>
        )}
        <label
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#76705F",
            display: "flex",
            alignItems: "center",
            gap: 6,
            minHeight: 24,
            cursor: "pointer",
          }}
        >
          <input type="checkbox" checked={item.active} onChange={() => onPatch(item, { active: !item.active })} />
          Active
        </label>
        {isEditable && (
          <button
            onClick={onExpand}
            data-testid={`service-expand-${item.id}`}
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#B08D55",
              textDecoration: "underline",
              textUnderlineOffset: 2,
              minHeight: 24,
              padding: "4px 2px",
              transition: "color .15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#8E7044")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#B08D55")}
          >
            {expanded ? "Hide" : "Starters"}
          </button>
        )}
        <button
          onClick={() => onRemove(item)}
          title="Delete"
          style={{
            color: "#A39A8B",
            minWidth: 24,
            minHeight: 24,
            padding: 5,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "color .15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#A35C45")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#A39A8B")}
        >
          <Trash2 size={13} />
        </button>
      </div>
      {expanded && isEditable && (
        <div
          data-testid={`service-defaults-${item.id}`}
          style={{
            padding: "12px 14px",
            background: "#FAF6EC",
            borderTop: "1px solid #EDE5D8",
            display: "flex", flexDirection: "column", gap: 8,
          }}
        >
          <div
            data-testid={`service-starters-title-${item.id}`}
            style={{
              fontSize: 10.5, fontWeight: 600, letterSpacing: "0.16em",
              textTransform: "uppercase", color: "#B08D55",
            }}
          >
            Starter deliverables
          </div>
          <p
            data-testid={`service-starters-hint-${item.id}`}
            style={{
              fontSize: 11, fontStyle: "italic", color: "#76705F",
              margin: 0, lineHeight: 1.45,
            }}
          >
            Studio-wide starters. Salesperson can override quantities per project in the Onboarding wizard (Step 3).
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.5fr 0.5fr 0.7fr 0.7fr auto",
              gap: 8,
              fontSize: 10, fontWeight: 600, letterSpacing: "0.16em",
              textTransform: "uppercase", color: "#A39A8B",
            }}
          >
            <div>Deliverable type</div>
            <div>Qty</div>
            <div>Unit</div>
            <div>Days from shoot</div>
            <div></div>
          </div>
          {defs.map((d, i) => (
            <div
              key={i}
              data-testid={`default-deliverable-row-${i}`}
              style={{
                display: "grid",
                gridTemplateColumns: "1.5fr 0.5fr 0.7fr 0.7fr auto",
                gap: 8,
                alignItems: "center",
              }}
            >
              <input
                placeholder="Type"
                value={d.deliverable_type || d.label || ""}
                onChange={(e) => updateDef(i, { deliverable_type: e.target.value })}
                className="sd-fld"
              />
              <input
                type="number" placeholder="Qty"
                value={d.quantity ?? d.default_qty ?? ""}
                onChange={(e) => updateDef(i, { quantity: parseInt(e.target.value || "0", 10) })}
                className="sd-fld"
              />
              <select
                value={d.unit || "photos"}
                onChange={(e) => updateDef(i, { unit: e.target.value })}
                className="sd-fld"
              >
                {DELIVERABLE_UNITS.map((u) => <option key={u}>{u}</option>)}
              </select>
              <input
                type="number" placeholder="Days"
                value={d.due_days_from_shoot ?? d.due_days ?? ""}
                onChange={(e) => updateDef(i, { due_days_from_shoot: parseInt(e.target.value || "0", 10) })}
                className="sd-fld"
              />
              <button
                type="button"
                onClick={() => removeDef(i)}
                data-testid={`default-deliverable-delete-${i}`}
                title="Remove"
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  minWidth: 26, minHeight: 26, padding: 4,
                  background: "transparent", color: "#A39A8B",
                  border: "1px solid transparent", borderRadius: 5, cursor: "pointer",
                  transition: "color .15s, border-color .15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#A35C45"; e.currentTarget.style.borderColor = "#F3D0C6"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "#A39A8B"; e.currentTarget.style.borderColor = "transparent"; }}
              >
                <X size={13} />
              </button>
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, paddingTop: 2 }}>
            <button
              type="button"
              onClick={addDef}
              data-testid={`service-add-default-${item.id}`}
              style={{
                fontSize: 10.5, fontWeight: 600, letterSpacing: "0.13em",
                textTransform: "uppercase", color: "#76705F",
                background: "transparent", border: "1px solid transparent",
                borderRadius: 6, padding: "6px 10px", cursor: "pointer", minHeight: 28,
                transition: "border-color .15s, color .15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#E4DCCD"; e.currentTarget.style.color = "#22382C"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.color = "#76705F"; }}
            >
              + Add deliverable
            </button>
            <button
              type="button"
              onClick={saveDefs}
              data-testid={`service-save-defaults-${item.id}`}
              style={{
                marginLeft: "auto",
                padding: "7px 14px", minHeight: 28,
                fontSize: 10.5, fontWeight: 600, letterSpacing: "0.13em",
                textTransform: "uppercase",
                background: "#22382C", color: "#F4EFE4",
                border: "1px solid #22382C", borderRadius: 6, cursor: "pointer",
                transition: "background .15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#2C4A3B")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#22382C")}
            >
              Save
            </button>
          </div>
          <style>{`
            .sd-fld{
              width:100%; padding:7px 9px;
              background:#FFFFFF; border:1px solid #E4DCCD; border-radius:7px;
              font-family:var(--ds-body-font);
              font-size:12.5px; color:#2A2722; outline:none;
              transition:border-color .15s, box-shadow .15s;
            }
            .sd-fld:focus{
              border-color:#B08D55;
              box-shadow:0 0 0 3px rgba(176,141,85,.14);
            }
          `}</style>
        </div>
      )}
    </div>
  );
}

function VendorsTab() {
  const [items, setItems] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [q, setQ] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null); // { mode: 'create' | 'edit', vendor }
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const refresh = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (serviceFilter) params.service = serviceFilter;
    if (showInactive) params.active = false;
    const [{ data: vd }, { data: cd }] = await Promise.all([
      api.get("/vendors", { params }),
      api.get("/catalogs"),
    ]);
    setItems(vd.items || []);
    setServiceTypes((cd.service_types || []).filter((s) => s.active).map((s) => s.name));
  };

  useEffect(() => { refresh(); }, [q, serviceFilter, showInactive]);

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.name}"?`)) return;
    const { data } = await api.delete(`/vendors/${v.id}`);
    toast.info(data.soft_deactivated ? `Soft-deactivated (in use).` : "Deleted.");
    refresh();
  };

  if (isMobile) {
    // ============ MOBILE BRANCH ============
    return (
      <div data-testid="tab-vendors">
        {/* Search + filter-sheet trigger */}
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 9,
              background: "#FFFDFA",
              border: "1px solid rgba(34,56,44,0.10)",
              borderRadius: 14,
              padding: "0 14px",
              height: 46,
            }}
          >
            <Search size={17} color="rgba(34,56,44,0.4)" strokeWidth={2} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search vendors"
              data-testid="vendors-search"
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 13.5,
                color: "#22382C",
              }}
            />
          </div>
          <button
            onClick={() => setFilterSheetOpen(true)}
            data-testid="vendors-filter-btn"
            aria-label="Filter vendors"
            style={{
              width: 46,
              height: 46,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#FFFDFA",
              border: "1px solid rgba(34,56,44,0.10)",
              borderRadius: 14,
              cursor: "pointer",
              color: "#22382C",
            }}
          >
            <SlidersHorizontal size={18} strokeWidth={2} />
          </button>
        </div>
        {/* +Add vendor full-width */}
        <button
          onClick={() => setModal({ mode: "create" })}
          data-testid="vendors-add-btn"
          style={{
            width: "100%",
            background: "#22382C",
            color: "#F7F3EC",
            border: "none",
            borderRadius: 13,
            height: 46,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.05em",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            cursor: "pointer",
            marginBottom: 18,
          }}
        >
          <Plus size={14} /> Add vendor
        </button>
        {/* Vendor cards */}
        <div data-testid="vendors-rows" style={{ display: "flex", flexDirection: "column", gap: 11 }}>
          {items.length === 0 ? (
            <div
              style={{
                padding: "44px 16px",
                textAlign: "center",
                fontSize: 13,
                fontStyle: "italic",
                color: "rgba(34,56,44,0.5)",
                background: "#FBF8F1",
                border: "1.5px dashed rgba(34,56,44,0.15)",
                borderRadius: 16,
              }}
            >
              No vendors yet.
            </div>
          ) : items.map((v) => (
            <VendorCardMobile
              key={v.id}
              vendor={v}
              onOpen={() => navigate(`/vendors/${v.id}`)}
              onEdit={() => setModal({ mode: "edit", vendor: v })}
              onDelete={() => remove(v)}
              onToggleActive={async () => { await api.patch(`/vendors/${v.id}`, { active: !v.active }); refresh(); }}
            />
          ))}
        </div>

        {filterSheetOpen && (
          <VendorFilterSheet
            serviceTypes={serviceTypes}
            serviceFilter={serviceFilter}
            setServiceFilter={setServiceFilter}
            showInactive={showInactive}
            setShowInactive={setShowInactive}
            onClose={() => setFilterSheetOpen(false)}
          />
        )}
        {modal && <VendorModal mode={modal.mode} vendor={modal.vendor} serviceTypes={serviceTypes} onClose={() => { setModal(null); refresh(); }} />}
      </div>
    );
  }

  // ============ DESKTOP BRANCH (original) ============
  return (
    <div data-testid="tab-vendors">
      {/* Filter row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search vendors…"
          data-testid="vendors-search"
          style={{
            background: "#FCFAF5",
            border: "1px solid #E4DCCD",
            borderRadius: 8,
            padding: "9px 12px",
            fontSize: 13,
            width: 260,
            color: "#2A2722",
            outline: "none",
          }}
        />
        {/* Native <select> preserved — no custom chevron added */}
        <select
          value={serviceFilter}
          onChange={(e) => setServiceFilter(e.target.value)}
          data-testid="vendors-service-filter"
          style={{
            background: "#FCFAF5",
            border: "1px solid #E4DCCD",
            borderRadius: 8,
            padding: "9px 12px",
            paddingRight: 32,
            fontSize: 13,
            color: "#22382C",
            cursor: "pointer",
            outline: "none",
          }}
        >
          <option value="">All services</option>
          {serviceTypes.map((s) => <option key={s}>{s}</option>)}
        </select>
        <label
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#76705F",
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            data-testid="vendors-show-inactive"
          />
          Show inactive
        </label>
        <button
          onClick={() => setModal({ mode: "create" })}
          data-testid="vendors-add-btn"
          style={{
            marginLeft: "auto",
            background: "#22382C",
            color: "#F4EFE4",
            border: "none",
            borderRadius: 6,
            padding: "10px 18px",
            fontSize: 13,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
          }}
        >
          <Plus size={14} /> Add vendor
        </button>
      </div>
      {/* Column header row */}
      <div
        className="catalog-col-header"
        style={{
          display: "grid",
          gridTemplateColumns: "1.8fr 0.9fr 1fr 1.5fr 0.8fr 80px 80px",
          gap: 14,
          alignItems: "center",
          padding: "0 18px 10px",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.13em",
          textTransform: "uppercase",
          color: "#A39A8B",
        }}
      >
        <div>Name</div>
        <div>Type</div>
        <div>Phone</div>
        <div>Services</div>
        <div>City</div>
        <div>Active</div>
        <div />
      </div>
      <div data-testid="vendors-rows" style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {items.length === 0 ? (
          <div
            style={{
              padding: 56,
              textAlign: "center",
              fontSize: 14,
              fontStyle: "italic",
              color: "#76705F",
              background: "#FAF6EC",
              border: "1px dashed #E4DCCD",
              borderRadius: 10,
            }}
          >
            No vendors yet.
          </div>
        ) : (
          items.map((v) => (
            <div
              key={v.id}
              data-testid={`vendor-row-${v.id}`}
              className="catalog-grid-row"
              style={{
                display: "grid",
                gridTemplateColumns: "1.8fr 0.9fr 1fr 1.5fr 0.8fr 80px 80px",
                gap: 14,
                alignItems: "center",
                background: "#FFFFFF",
                border: "1px solid #EDE5D8",
                borderRadius: 10,
                padding: "14px 18px",
                boxShadow: "0 2px 6px rgba(60,46,20,.05)",
                transition: "box-shadow .15s ease, transform .15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 5px 14px rgba(60,46,20,.10)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div style={{ minWidth: 0 }}>
                <button
                  onClick={() => navigate(`/vendors/${v.id}`)}
                  data-testid={`vendor-open-${v.id}`}
                  style={{
                    textAlign: "left",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#2A2722",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                    transition: "color .15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#22382C")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#2A2722")}
                >
                  {v.name}
                </button>
              </div>
              <div style={{ fontSize: 12, color: "#76705F" }}>
                {VENDOR_TYPE_LABELS[v.type]}{v.type === "agency" ? ` · ${v.crew_capacity}` : ""}
              </div>
              <div style={{ fontSize: 12, color: "#76705F" }}>{v.phone_display || "—"}</div>
              <div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {(v.services || []).slice(0, 3).map((s) => (
                    <span
                      key={s}
                      style={{
                        background: "#F1EFE6",
                        color: "#5C6B5E",
                        fontSize: 9,
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        padding: "3px 7px",
                        borderRadius: 5,
                      }}
                    >
                      {s}
                    </span>
                  ))}
                  {(v.services || []).length > 3 && (
                    <span
                      style={{
                        background: "#F1EFE6",
                        color: "#5C6B5E",
                        fontSize: 9,
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        padding: "3px 7px",
                        borderRadius: 5,
                      }}
                    >
                      +{v.services.length - 3}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#76705F" }}>{v.city || "—"}</div>
              <div>
                <input
                  type="checkbox"
                  checked={v.active}
                  onChange={async () => { await api.patch(`/vendors/${v.id}`, { active: !v.active }); refresh(); }}
                  data-testid={`vendor-active-${v.id}`}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                <button
                  onClick={() => setModal({ mode: "edit", vendor: v })}
                  data-testid={`vendor-edit-${v.id}`}
                  style={{
                    color: "#A39A8B",
                    minWidth: 24,
                    minHeight: 24,
                    padding: 5,
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "color .15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#22382C")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#A39A8B")}
                >
                  <Edit2 size={13} />
                </button>
                <button
                  onClick={() => remove(v)}
                  data-testid={`vendor-delete-${v.id}`}
                  style={{
                    color: "#A39A8B",
                    minWidth: 24,
                    minHeight: 24,
                    padding: 5,
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "color .15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#A35C45")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#A39A8B")}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {modal && <VendorModal mode={modal.mode} vendor={modal.vendor} serviceTypes={serviceTypes} onClose={() => { setModal(null); refresh(); }} />}
    </div>
  );
}

// Pill toggle switch used by Vendor/Editor mobile cards (visual only —
// wraps a native <input type=checkbox> so a11y + testid semantics survive).
function ToggleSwitch({ checked, onChange, testId, ariaLabel }) {
  return (
    <label
      style={{
        position: "relative",
        display: "inline-block",
        width: 44,
        height: 26,
        flexShrink: 0,
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        data-testid={testId}
        aria-label={ariaLabel}
        style={{ opacity: 0, width: 0, height: 0 }}
      />
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 999,
          background: checked ? "#22382C" : "rgba(34,56,44,0.15)",
          transition: "background .15s",
        }}
      />
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 21 : 3,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#FFFDFA",
          transition: "left .15s",
        }}
      />
    </label>
  );
}

function VendorCardMobile({ vendor, onOpen, onEdit, onDelete, onToggleActive }) {
  const v = vendor;
  // Build meta line — Individual / Agency · N crew · City · Phone (live)
  const parts = [];
  const typeLabel = VENDOR_TYPE_LABELS[v.type] || v.type || "";
  if (typeLabel) parts.push(typeLabel + (v.type === "agency" && v.crew_capacity ? ` · ${v.crew_capacity}` : ""));
  if (v.city) parts.push(v.city);
  if (v.phone_display || v.phone) parts.push(v.phone_display || v.phone);
  return (
    <div
      data-testid={`vendor-row-${v.id}`}
      style={{
        background: "#FFFDFA",
        border: "1px solid rgba(34,56,44,0.10)",
        borderRadius: 16,
        padding: 15,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
        <button
          onClick={onOpen}
          data-testid={`vendor-open-${v.id}`}
          style={{
            fontFamily: "var(--ds-num-font)",
            fontSize: 17,
            fontWeight: 500,
            color: "#22382C",
            textAlign: "left",
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {v.name}
        </button>
        <ToggleSwitch
          checked={!!v.active}
          onChange={onToggleActive}
          testId={`vendor-active-${v.id}`}
          ariaLabel={`Toggle ${v.name} active`}
        />
      </div>
      {parts.length > 0 && (
        <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(34,56,44,0.5)", marginBottom: (v.services || []).length ? 10 : 12 }}>
          {parts.join(" · ")}
        </div>
      )}
      {(v.services || []).length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {(v.services || []).map((s) => (
            <span
              key={s}
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: "rgba(34,56,44,0.7)",
                background: "rgba(34,56,44,0.06)",
                padding: "4px 10px",
                borderRadius: 999,
              }}
            >
              {s}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 14, paddingTop: 11, borderTop: "1px solid rgba(34,56,44,0.08)" }}>
        <button
          onClick={onEdit}
          data-testid={`vendor-edit-${v.id}`}
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#B08D55",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "6px 2px",
            minHeight: 32,
          }}
        >
          Edit
        </button>
        <button
          onClick={onDelete}
          data-testid={`vendor-delete-${v.id}`}
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#A24B3B",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "6px 2px",
            minHeight: 32,
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function VendorFilterSheet({ serviceTypes, serviceFilter, setServiceFilter, showInactive, setShowInactive, onClose }) {
  return (
    <>
      <div
        onClick={onClose}
        data-testid="vendors-filter-scrim"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 10040,
          background: "rgba(20,30,24,0.34)",
          backdropFilter: "blur(2px)",
        }}
      />
      <div
        data-testid="vendors-filter-sheet"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10050,
          background: "#F7F3EC",
          borderRadius: "26px 26px 0 0",
          boxShadow: "0 -20px 50px -10px rgba(20,30,24,0.35)",
          padding: "12px 20px calc(env(safe-area-inset-bottom, 0px) + 26px)",
          isolation: "isolate",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 40,
            height: 5,
            borderRadius: 999,
            background: "rgba(34,56,44,0.18)",
            margin: "0 auto 16px",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontFamily: "var(--ds-num-font)", fontSize: 22, fontWeight: 500, color: "#22382C" }}>
            Filter vendors
          </span>
          <button
            onClick={onClose}
            aria-label="Close filter"
            style={{
              width: 44,
              height: 44,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: 20,
              color: "rgba(34,56,44,0.4)",
            }}
          >
            ×
          </button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#FFFDFA",
            border: "1px solid rgba(34,56,44,0.10)",
            borderRadius: 13,
            padding: "0 15px",
            height: 52,
            marginBottom: 10,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: "#22382C" }}>Service</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              data-testid="vendors-service-filter"
              style={{
                appearance: "none",
                WebkitAppearance: "none",
                background: "transparent",
                border: "none",
                fontSize: 13.5,
                fontWeight: 600,
                color: serviceFilter ? "#22382C" : "rgba(34,56,44,0.55)",
                cursor: "pointer",
                paddingRight: 20,
                outline: "none",
                minHeight: 44,
                textAlign: "right",
                textAlignLast: "right",
              }}
            >
              <option value="">All services</option>
              {serviceTypes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={14} color="#B08D55" style={{ position: "absolute", right: 0, pointerEvents: "none" }} />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#FFFDFA",
            border: "1px solid rgba(34,56,44,0.10)",
            borderRadius: 13,
            padding: "0 15px",
            height: 52,
            marginBottom: 22,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: "#22382C" }}>Show inactive</span>
          <ToggleSwitch
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            testId="vendors-show-inactive"
            ariaLabel="Show inactive vendors"
          />
        </div>
        <button
          onClick={onClose}
          data-testid="vendors-filter-apply"
          style={{
            width: "100%",
            background: "#22382C",
            color: "#F7F3EC",
            border: "none",
            padding: "15px",
            borderRadius: 14,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.06em",
            cursor: "pointer",
            minHeight: 48,
          }}
        >
          Apply
        </button>
      </div>
    </>
  );
}

function EditorCardMobile({ editor, onOpen, onEdit, onReset, onToggleActive }) {
  const e = editor;
  const parts = [];
  if (e.city) parts.push(e.city);
  if (e.phone_display || e.phone) parts.push(e.phone_display || e.phone);
  return (
    <div
      data-testid={`editor-row-${e.id}`}
      style={{
        background: "#FFFDFA",
        border: "1px solid rgba(34,56,44,0.10)",
        borderRadius: 16,
        padding: 15,
        opacity: e.active ? 1 : 0.6,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0, flex: 1 }}>
          <button
            onClick={onOpen}
            data-testid={`editor-open-${e.id}`}
            style={{
              fontFamily: "var(--ds-num-font)",
              fontSize: 17,
              fontWeight: 500,
              color: "#22382C",
              textAlign: "left",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {e.full_name}
          </button>
          <SpecialtyPill specialty={e.specialty} />
        </div>
        <ToggleSwitch
          checked={!!e.active}
          onChange={onToggleActive}
          testId={`editor-active-${e.id}`}
          ariaLabel={`Toggle ${e.full_name} active`}
        />
      </div>
      {parts.length > 0 && (
        <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(34,56,44,0.5)", marginBottom: 12 }}>
          {parts.join(" · ")}
        </div>
      )}
      <div style={{ display: "flex", gap: 14, paddingTop: 11, borderTop: "1px solid rgba(34,56,44,0.08)" }}>
        <button
          onClick={onEdit}
          data-testid={`editor-edit-${e.id}`}
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#B08D55",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "6px 2px",
            minHeight: 32,
          }}
        >
          Edit
        </button>
        <button
          onClick={onReset}
          data-testid={`editor-reset-${e.id}`}
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#95643C",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "6px 2px",
            minHeight: 32,
          }}
        >
          Reset Password
        </button>
      </div>
    </div>
  );
}


function VendorModal({ mode, vendor, serviceTypes, onClose }) {
  const [form, setForm] = useState({
    name: vendor?.name || "",
    phone: vendor?.phone_display || vendor?.phone || "",
    email: vendor?.email || "",
    type: vendor?.type || "individual",
    crew_capacity: vendor?.crew_capacity || 1,
    services: vendor?.services || [],
    city: vendor?.city || "",
    notes: vendor?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const toggleSvc = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const body = { ...form };
      if (body.type === "individual") body.crew_capacity = 1;
      if (mode === "create") await api.post("/vendors", body);
      else await api.patch(`/vendors/${vendor.id}`, body);
      toast.success(mode === "create" ? "Vendor added" : "Vendor saved");
      onClose();
    } catch (err) {
      const det = err?.response?.data?.detail;
      if (typeof det === "object" && det?.message) toast.error(det.message);
      else toast.error(det || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid="vendor-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        background: "rgba(34,56,44,0.42)",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          width: "100%", maxWidth: 560, maxHeight: "90vh",
          overflowY: "auto",
          border: "1px solid #E9E2D6",
          borderTop: "3px solid #22382C",
          borderRadius: 14,
          boxShadow: "0 20px 44px rgba(34,56,44,.20)",
        }}
      >
        <div style={{ padding: 24 }}>
          <div
            style={{
              display: "flex", alignItems: "flex-start", justifyContent: "space-between",
              gap: 12, marginBottom: 18,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div className="cm-eyebrow">{mode === "create" ? "ADD" : "EDIT"}</div>
              <h2
                className="font-display"
                style={{ fontSize: 24, color: "#22382C", margin: "4px 0 0", lineHeight: 1.15 }}
              >
                {mode === "create" ? "New vendor" : form.name}
              </h2>
            </div>
            <button
              type="button" onClick={onClose}
              className="cm-icon-btn"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="Name">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="cm-fld"
                data-testid="vendor-modal-name"
              />
            </Field>
            <Field label="Phone">
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="cm-fld"
                data-testid="vendor-modal-phone"
              />
            </Field>
            <Field label="Email">
              <input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="cm-fld"
                data-testid="vendor-modal-email"
              />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Field label="Type">
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value, crew_capacity: e.target.value === "individual" ? 1 : form.crew_capacity })}
                  className="cm-fld"
                  data-testid="vendor-modal-type"
                >
                  <option value="individual">Individual</option>
                  <option value="agency">Agency</option>
                </select>
              </Field>
              <Field label="Crew capacity">
                <input
                  type="number" min="1"
                  disabled={form.type === "individual"}
                  value={form.crew_capacity}
                  onChange={(e) => setForm({ ...form, crew_capacity: parseInt(e.target.value || "1", 10) })}
                  className="cm-fld"
                  data-testid="vendor-modal-capacity"
                />
              </Field>
            </div>
            <Field label="Services">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {serviceTypes.map((s) => {
                  const active = form.services.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleSvc(s)}
                      data-testid={`vendor-svc-${s}`}
                      style={{
                        padding: "5px 11px",
                        fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
                        background: active ? "#22382C" : "#FFFFFF",
                        color: active ? "#F4EFE4" : "#2A2722",
                        border: `1px solid ${active ? "#22382C" : "#E4DCCD"}`,
                        borderRadius: 999, cursor: "pointer",
                        transition: "background .15s, border-color .15s, color .15s",
                      }}
                      onMouseEnter={(e) => {
                        if (!active) { e.currentTarget.style.borderColor = "#B08D55"; }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) { e.currentTarget.style.borderColor = "#E4DCCD"; }
                      }}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="City">
              <input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="cm-fld"
                data-testid="vendor-modal-city"
              />
            </Field>
            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows="2"
                className="cm-fld"
                data-testid="vendor-modal-notes"
              />
            </Field>
          </div>
        </div>
        <div
          style={{
            display: "flex", justifyContent: "flex-end", gap: 10,
            padding: "14px 24px",
            borderTop: "1px solid #EDE5D8",
            background: "#FAF6EC",
          }}
        >
          <button
            type="button" onClick={onClose}
            className="cm-ghost-btn"
          >
            Cancel
          </button>
          <button
            type="button" onClick={save} disabled={saving}
            data-testid="vendor-modal-save"
            className="cm-primary-btn"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 10.5, fontWeight: 600, letterSpacing: "0.16em",
          textTransform: "uppercase", color: "#A39A8B", marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
      <style>{`
        .cm-eyebrow{
          font-size:10.5px; font-weight:700; letter-spacing:0.16em;
          text-transform:uppercase; color:#B08D55;
        }
        .cm-fld{
          width:100%; padding:10px 12px;
          background:#FCFAF5; border:1px solid #E4DCCD; border-radius:8px;
          font-family:var(--ds-body-font);
          font-size:14px; color:#2A2722; outline:none;
          transition:border-color .15s, box-shadow .15s;
        }
        .cm-fld:focus{
          border-color:#B08D55;
          box-shadow:0 0 0 3px rgba(176,141,85,.14);
        }
        .cm-fld:disabled{background:#EFE8DC; color:#B7AE9E; cursor:not-allowed;}
        .cm-primary-btn{
          padding:11px 18px; font-size:11px; font-weight:600;
          letter-spacing:0.13em; text-transform:uppercase;
          background:#22382C; color:#F4EFE4;
          border:1px solid #22382C; border-radius:6px;
          cursor:pointer; transition:background .15s;
        }
        .cm-primary-btn:hover:not(:disabled){background:#2C4A3B;}
        .cm-primary-btn:disabled{background:#EFE8DC; color:#B7AE9E; border-color:#EFE8DC; cursor:not-allowed;}
        .cm-ghost-btn{
          padding:11px 18px; font-size:11px; font-weight:600;
          letter-spacing:0.13em; text-transform:uppercase;
          background:#FFFFFF; color:#76705F;
          border:1px solid #E4DCCD; border-radius:6px;
          cursor:pointer; transition:border-color .15s, color .15s;
        }
        .cm-ghost-btn:hover{border-color:#B08D55; color:#22382C;}
        .cm-danger-btn{
          padding:11px 18px; font-size:11px; font-weight:600;
          letter-spacing:0.13em; text-transform:uppercase;
          background:#A35C45; color:#FFFFFF;
          border:1px solid #A35C45; border-radius:6px;
          cursor:pointer; transition:background .15s;
        }
        .cm-danger-btn:hover:not(:disabled){background:#8E4A37; border-color:#8E4A37;}
        .cm-danger-btn:disabled{background:#EFE8DC; color:#B7AE9E; border-color:#EFE8DC; cursor:not-allowed;}
        .cm-icon-btn{
          display:inline-flex; align-items:center; justify-content:center;
          min-width:28px; min-height:28px; padding:5px;
          background:transparent; color:#A39A8B;
          border:1px solid transparent; border-radius:6px;
          cursor:pointer; transition:color .15s, border-color .15s, background .15s;
        }
        .cm-icon-btn:hover{color:#22382C; border-color:#E4DCCD; background:#FAF6EC;}
      `}</style>
    </label>
  );
}


function EditorsTab() {
  const [items, setItems] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null); // null | { mode: 'create' } | { mode: 'edit', editor } | { mode: 'reset', editor }
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const refresh = async () => {
    const { data } = await api.get("/editors", { params: { include_inactive: showInactive } });
    setItems(data.items || []);
  };

  useEffect(() => { refresh(); }, [showInactive]);

  const toggleActive = async (e) => {
    await api.patch(`/editors/${e.id}`, { active: !e.active });
    refresh();
  };

  // Active first, then inactive (with greyed visual via opacity)
  const sorted = [...items].sort((a, b) => {
    if (!!a.active !== !!b.active) return a.active ? -1 : 1;
    return (a.full_name || "").localeCompare(b.full_name || "");
  });

  if (isMobile) {
    return (
      <div data-testid="tab-editors">
        {/* Show inactive as a row card */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#FFFDFA",
            border: "1px solid rgba(34,56,44,0.10)",
            borderRadius: 13,
            padding: "0 15px",
            height: 48,
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: "#22382C" }}>Show inactive</span>
          <ToggleSwitch
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            testId="editors-show-inactive"
            ariaLabel="Show inactive editors"
          />
        </div>
        {/* +Add editor full-width */}
        <button
          onClick={() => setModal({ mode: "create" })}
          data-testid="editors-add-btn"
          style={{
            width: "100%",
            background: "#22382C",
            color: "#F7F3EC",
            border: "none",
            borderRadius: 13,
            height: 46,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.05em",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            cursor: "pointer",
            marginBottom: 18,
          }}
        >
          <Plus size={14} /> Add editor
        </button>
        {/* Editor cards */}
        <div data-testid="editors-rows" style={{ display: "flex", flexDirection: "column", gap: 11 }}>
          {sorted.length === 0 ? (
            <div
              style={{
                padding: "44px 16px",
                textAlign: "center",
                fontSize: 13,
                fontStyle: "italic",
                color: "rgba(34,56,44,0.5)",
                background: "#FBF8F1",
                border: "1.5px dashed rgba(34,56,44,0.15)",
                borderRadius: 16,
              }}
            >
              No editors yet. Tap &quot;Add editor&quot; to create one.
            </div>
          ) : sorted.map((e) => (
            <EditorCardMobile
              key={e.id}
              editor={e}
              onOpen={() => navigate(`/editors/${e.id}`)}
              onEdit={() => setModal({ mode: "edit", editor: e })}
              onReset={() => setModal({ mode: "reset", editor: e })}
              onToggleActive={() => toggleActive(e)}
            />
          ))}
        </div>

        {modal?.mode === "create" && <EditorModal onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
        {modal?.mode === "edit" && <EditorModal editor={modal.editor} onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
        {modal?.mode === "reset" && <ResetPasswordModal editor={modal.editor} onClose={() => setModal(null)} onSaved={() => setModal(null)} />}
      </div>
    );
  }

  return (
    <div data-testid="tab-editors">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <label
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#76705F",
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            data-testid="editors-show-inactive"
          />
          Show inactive
        </label>
        <button
          onClick={() => setModal({ mode: "create" })}
          data-testid="editors-add-btn"
          style={{
            marginLeft: "auto",
            background: "#22382C",
            color: "#F4EFE4",
            border: "none",
            borderRadius: 6,
            padding: "10px 18px",
            fontSize: 13,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
          }}
        >
          <Plus size={14} /> Add editor
        </button>
      </div>

      {/* Column header row */}
      <div
        className="catalog-col-header"
        style={{
          display: "grid",
          gridTemplateColumns: "1.8fr 1fr 1fr 1fr 0.8fr 220px",
          gap: 14,
          alignItems: "center",
          padding: "0 18px 10px",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.13em",
          textTransform: "uppercase",
          color: "#A39A8B",
        }}
      >
        <div>Name</div>
        <div>Specialty</div>
        <div>Phone</div>
        <div>City</div>
        <div>Active</div>
        <div />
      </div>
      <div data-testid="editors-rows" style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {sorted.length === 0 && (
          <div
            style={{
              padding: 56,
              textAlign: "center",
              fontSize: 14,
              fontStyle: "italic",
              color: "#76705F",
              background: "#FAF6EC",
              border: "1px dashed #E4DCCD",
              borderRadius: 10,
            }}
          >
            No editors yet. Click &quot;Add editor&quot; to create one.
          </div>
        )}
        {sorted.map((e) => (
          <div
            key={e.id}
            data-testid={`editor-row-${e.id}`}
            className="catalog-grid-row"
            style={{
              display: "grid",
              gridTemplateColumns: "1.8fr 1fr 1fr 1fr 0.8fr 220px",
              gap: 14,
              alignItems: "center",
              background: "#FFFFFF",
              border: "1px solid #EDE5D8",
              borderRadius: 10,
              padding: "7px 9px",
              boxShadow: "0 2px 6px rgba(60,46,20,.05)",
              opacity: e.active ? 1 : 0.55,
              transition: "box-shadow .15s ease, transform .15s ease",
            }}
            onMouseEnter={(ev) => {
              ev.currentTarget.style.boxShadow = "0 5px 14px rgba(60,46,20,.10)";
              ev.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(ev) => {
              ev.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
              ev.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <div style={{ minWidth: 0 }}>
              <button
                onClick={() => navigate(`/editors/${e.id}`)}
                data-testid={`editor-open-${e.id}`}
                style={{
                  textAlign: "left",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#2A2722",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  transition: "color .15s",
                }}
                onMouseEnter={(ev) => (ev.currentTarget.style.color = "#22382C")}
                onMouseLeave={(ev) => (ev.currentTarget.style.color = "#2A2722")}
              >
                {e.full_name}
              </button>
            </div>
            <div>
              <SpecialtyPill specialty={e.specialty} />
            </div>
            <div style={{ fontSize: 12, color: "#76705F" }}>{e.phone_display || "—"}</div>
            <div style={{ fontSize: 12, color: "#76705F" }}>{e.city || "—"}</div>
            <div>
              <input type="checkbox" checked={e.active} onChange={() => toggleActive(e)} data-testid={`editor-active-${e.id}`} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
              <button
                onClick={() => setModal({ mode: "edit", editor: e })}
                data-testid={`editor-edit-${e.id}`}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#B08D55",
                  minHeight: 24,
                  padding: "4px 2px",
                  transition: "color .15s",
                }}
                onMouseEnter={(ev) => (ev.currentTarget.style.color = "#8E7044")}
                onMouseLeave={(ev) => (ev.currentTarget.style.color = "#B08D55")}
              >
                Edit
              </button>
              <button
                onClick={() => setModal({ mode: "reset", editor: e })}
                data-testid={`editor-reset-${e.id}`}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#B08D55",
                  minHeight: 24,
                  padding: "4px 2px",
                  transition: "color .15s",
                }}
                onMouseEnter={(ev) => (ev.currentTarget.style.color = "#8E7044")}
                onMouseLeave={(ev) => (ev.currentTarget.style.color = "#B08D55")}
              >
                Reset password
              </button>
            </div>
          </div>
        ))}
      </div>

      {modal?.mode === "create" && <EditorModal onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
      {modal?.mode === "edit" && <EditorModal editor={modal.editor} onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
      {modal?.mode === "reset" && <ResetPasswordModal editor={modal.editor} onClose={() => setModal(null)} onSaved={() => setModal(null)} />}
    </div>
  );
}

function SpecialtyPill({ specialty }) {
  // Phase 7.0 Catalog Editors — per-specialty chip palette
  const palette = {
    photo: { bg: "#F6EAD3", fg: "#B08234", label: "Photo" },
    video: { bg: "#E4EDDF", fg: "#5E7E58", label: "Video" },
    both:  { bg: "#ECE3F4", fg: "#80619E", label: "Both" },
  };
  const p = palette[specialty] || palette.photo;
  return (
    <span
      data-testid={`specialty-pill-${specialty}`}
      style={{
        display: "inline-block",
        background: p.bg,
        color: p.fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        padding: "3px 7px",
        borderRadius: 6,
      }}
    >
      {p.label}
    </span>
  );
}

function EditorModal({ editor, onClose, onSaved }) {
  const isEdit = !!editor;
  const [fullName, setFullName] = useState(editor?.full_name || "");
  const [phone, setPhone] = useState(editor?.phone_display || editor?.phone || "");
  const [email, setEmail] = useState(editor?.email || "");
  const [username, setUsername] = useState(editor?.username || "");
  const [password, setPassword] = useState("");
  const [specialty, setSpecialty] = useState(editor?.specialty || "photo");
  const [city, setCity] = useState(editor?.city || "");

  const save = async () => {
    if (!fullName.trim()) { toast.error("Full name required"); return; }
    try {
      if (isEdit) {
        await api.patch(`/editors/${editor.id}`, {
          full_name: fullName.trim(), phone: phone || null, email: email || null,
          specialty, city: city || null,
        });
      } else {
        if (!username.trim()) { toast.error("Username required"); return; }
        if (!password || password.length < 8) { toast.error("Password must be at least 8 chars"); return; }
        await api.post("/editors", {
          full_name: fullName.trim(), phone: phone || null, email: email || null,
          username: username.trim(), password,
          specialty, city: city || null,
        });
      }
      toast.success(isEdit ? "Editor updated" : "Editor created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    // Phase 5 Slice B-2 bug fix — modal only closes on explicit Save success
    // or Cancel button click. Backdrop click + Enter key must NOT dismiss it.
    <div
      data-testid="editor-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        background: "rgba(34,56,44,0.42)",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
        style={{
          background: "#FFFFFF",
          width: "100%", maxWidth: 460, maxHeight: "90vh",
          overflowY: "auto",
          border: "1px solid #E9E2D6",
          borderTop: "3px solid #22382C",
          borderRadius: 14,
          boxShadow: "0 20px 44px rgba(34,56,44,.20)",
        }}
      >
        <div style={{ padding: 24 }}>
          <div className="cm-eyebrow" style={{ marginBottom: 4 }}>
            {isEdit ? "EDIT EDITOR" : "ADD EDITOR"}
          </div>
          <h2
            className="font-display"
            style={{ fontSize: 24, color: "#22382C", margin: "0 0 18px", lineHeight: 1.15 }}
          >
            {isEdit ? (editor.full_name || "Editor") : "New editor"}
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="Full name *">
              <input
                value={fullName} onChange={(e) => setFullName(e.target.value)}
                className="cm-fld" data-testid="editor-name-input"
              />
            </Field>
            <Field label="Phone">
              <input
                value={phone} onChange={(e) => setPhone(e.target.value)}
                className="cm-fld" data-testid="editor-phone-input"
              />
            </Field>
            <Field label="Email">
              <input
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="cm-fld" data-testid="editor-email-input"
              />
            </Field>
            {!isEdit && (
              <>
                <Field label="Username *">
                  <input
                    value={username} onChange={(e) => setUsername(e.target.value)}
                    className="cm-fld" data-testid="editor-username-input"
                  />
                </Field>
                <Field label="Password * (min 8)">
                  <input
                    type="password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="cm-fld" data-testid="editor-password-input"
                  />
                </Field>
              </>
            )}
            <Field label="Specialty">
              <div
                data-testid="editor-specialty-radio"
                style={{ display: "flex", gap: 14, alignItems: "center", minHeight: 30 }}
              >
                {["photo", "video", "both"].map((s) => (
                  <label
                    key={s}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      fontSize: 13, color: "#2A2722", cursor: "pointer", minHeight: 24,
                    }}
                  >
                    <input
                      type="radio" checked={specialty === s}
                      onChange={() => setSpecialty(s)}
                      data-testid={`editor-specialty-${s}`}
                    />
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="City">
              <input
                value={city} onChange={(e) => setCity(e.target.value)}
                className="cm-fld" data-testid="editor-city-input"
              />
            </Field>
          </div>
        </div>
        <div
          style={{
            display: "flex", justifyContent: "flex-end", gap: 10,
            padding: "14px 24px",
            borderTop: "1px solid #EDE5D8",
            background: "#FAF6EC",
          }}
        >
          <button
            type="button" onClick={onClose}
            className="cm-ghost-btn"
          >
            Cancel
          </button>
          <button
            type="button" onClick={save}
            data-testid="editor-save"
            className="cm-primary-btn"
          >
            {isEdit ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ editor, onClose, onSaved }) {
  const [password, setPassword] = useState("");
  const save = async () => {
    if (!password || password.length < 8) { toast.error("Password must be at least 8 chars"); return; }
    try {
      await api.post(`/editors/${editor.id}/reset-password`, { password });
      toast.success("Password reset");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div
      data-testid="reset-password-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        background: "rgba(34,56,44,0.42)",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
        style={{
          background: "#FFFFFF",
          width: "100%", maxWidth: 420,
          border: "1px solid #E9E2D6",
          borderTop: "3px solid #A35C45",
          borderRadius: 14,
          boxShadow: "0 20px 44px rgba(34,56,44,.20)",
        }}
      >
        <div style={{ padding: 24 }}>
          <div className="cm-eyebrow" style={{ color: "#A35C45", marginBottom: 4 }}>
            RESET PASSWORD
          </div>
          <h2
            className="font-display"
            style={{ fontSize: 22, color: "#22382C", margin: "0 0 16px", lineHeight: 1.15 }}
          >
            {editor.full_name}
          </h2>
          <Field label="New password (min 8)">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="cm-fld"
              data-testid="reset-password-input"
              autoFocus
            />
          </Field>
        </div>
        <div
          style={{
            display: "flex", justifyContent: "flex-end", gap: 10,
            padding: "14px 24px",
            borderTop: "1px solid #EDE5D8",
            background: "#FAF6EC",
          }}
        >
          <button
            type="button" onClick={onClose}
            className="cm-ghost-btn"
          >
            Cancel
          </button>
          <button
            type="button" onClick={save}
            data-testid="reset-password-save"
            className="cm-danger-btn"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
